import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import api from '../services/api';
import { PageHeader, Panel, States, selectClass } from '../components/OpsPageKit';

// Climb Check: tomorrow's ODIN jobs, each surveyed for how the crew gets up to
// the CBT. The survey runs on the tech server (services/climbCheck): Openreach
// pole record, inspection-date test status, manual overrides, and the drop
// route — direct, or via another pole when the direct line is blocked.

const CALLS = {
  hoist: { label: 'Hoist / MEWP', colour: '#b42318', bg: '#fdecea' },
  trees: { label: 'Trees: check', colour: '#b54708', bg: '#fef0e6' },
  confirm: { label: 'Confirm on site', colour: '#8a6100', bg: '#fdf6dd' },
  climb: { label: 'Climbable', colour: '#067647', bg: '#e6f6ee' },
  netomnia: { label: 'Netomnia pole', colour: '#3e4784', bg: '#eaecf5' },
  check: { label: 'Check manually', colour: '#475467', bg: '#eef0f3' },
  los: { label: 'LOS issue', colour: '#5925dc', bg: '#f0edfd' },
  not_viable: { label: 'Not viable', colour: '#7a2e0e', bg: '#f4ebe7' },
};
const ORDER = ['hoist', 'trees', 'los', 'confirm', 'check', 'netomnia', 'not_viable', 'climb'];
// A manager's Pole Survey of the job (opened from "Pole map") outranks the
// automatic call; "Other" keeps the automatic call and shows the note.
// Trees and line of sight (LOS) all sit under Check manually (Ruan,
// 19/10/2026), automatic or surveyed; the reason shows under the call.
const SURVEY_CALL = { climbable: 'climb', hoist: 'hoist', tree_work: 'check', los_issue: 'check', not_viable: 'not_viable' };
const CHECK_REASON = { trees: 'Trees on the drop line', los: 'Line of sight: building near the line', history: 'MEWP needed here before' };
const ukDay = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { timeZone: 'Europe/London' }) : '');
const SURVEY_LABEL = { climbable: 'Climbable', hoist: 'Hoist / MEWP required', tree_work: 'Tree work needed', los_issue: 'LOS issue', not_viable: 'Not viable', other: 'Other' };
const callOf = (j) => SURVEY_CALL[j.survey_outcome] || j.call_result;
const ukDateTime = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');
const ukDate = (iso) => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : '');
const weekday = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'long' }) : '');
const titleCase = (a) => {
  const parts = String(a || '').split(', ');
  const pc = /^[A-Z]{1,2}\d/.test(parts[parts.length - 1] || '') ? parts.pop() : null;
  const t = parts.join(', ').toLowerCase().replace(/\b([a-z])/g, (m) => m.toUpperCase());
  return pc ? `${t}, ${pc}` : t;
};

function CallPill({ call }) {
  const c = CALLS[call] || CALLS.check;
  return <span className="inline-block whitespace-nowrap rounded px-2 py-0.5 text-xs font-semibold" style={{ color: c.colour, background: c.bg }}>{c.label}</span>;
}

// When ODIN was last read, and a warning when a read is missing or failed. The
// laptop script reads today at 08:00 and tomorrow at 15:00; jobs more than
// survey_days ahead are listed but surveyed once they come into range.
const KIND_LABEL = { today: "today's jobs", next: "tomorrow's jobs", month: 'the month ahead', manual: 'a refresh' };
const hhmm = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');
function OdinStatus({ odin, date, onRefresh, note }) {
  if (!odin) return null;
  const runs = odin.runs || [];
  const lastOk = runs.find((r) => r.status === 'ok');
  const last = runs[0];
  const now = new Date();
  const today = now.toLocaleDateString('en-CA'); // YYYY-MM-DD, local
  const okToday = (kind) => runs.some((r) => r.status === 'ok' && r.kind === kind && new Date(r.started_at).toLocaleDateString('en-CA') === today);
  const mins = now.getHours() * 60 + now.getMinutes();
  let warn = null;
  if (last && last.status === 'signed_out') warn = 'ODIN signed out on the laptop: sign in to ODIN again there, then the next read runs on its own.';
  else if (last && last.status === 'failed') warn = `The last ODIN read failed${last.error ? `: ${last.error}` : ''}.`;
  else if (!lastOk) warn = 'ODIN has not been read yet. Check the laptop has Chrome open with a signed-in ODIN tab.';
  else if (mins >= 15 * 60 + 30 && !okToday('next')) warn = "Tomorrow's 15:00 ODIN read hasn't run. Is the laptop on with the ODIN tab open?";
  else if (mins >= 8 * 60 + 30 && !okToday('today')) warn = "Today's 08:00 ODIN read hasn't run. Is the laptop on with the ODIN tab open?";
  const ahead = date && (new Date(`${date}T12:00:00`) - now) / 864e5 > (odin.survey_days || 7);
  return (
    <div className={`mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-3 py-2 text-xs ${warn ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-gray-200 bg-white text-gray-600'}`}>
      <span className="min-w-0 flex-1">
        {warn ? <span className="font-semibold">{warn} </span> : null}
        {lastOk ? <>Last read from ODIN: <span className="font-medium text-gray-900">{hhmm(lastOk.finished_at || lastOk.started_at)}</span> ({KIND_LABEL[lastOk.kind] || lastOk.kind}, {lastOk.jobs} jobs{lastOk.added || lastOk.removed ? `, ${lastOk.added} added, ${lastOk.removed} removed` : ''})</> : null}
        {ahead ? <span className="block text-gray-500">This date is more than {odin.survey_days || 7} days away: its jobs are listed and surveyed once it comes within {odin.survey_days || 7} days.</span> : null}
        {odin.refresh_waiting ? <span className="block text-gray-500">A refresh is waiting for the laptop.</span> : null}
        {note ? <span className="block font-medium text-gray-900">{note}</span> : null}
      </span>
      {date ? (
        <button type="button" onClick={onRefresh} className="rounded-md border border-gray-300 bg-white px-2.5 py-1 font-semibold text-[#711C91] hover:bg-gray-50">
          Refresh from ODIN
        </button>
      ) : null}
    </div>
  );
}

export default function ClimbCheck() {
  const [dates, setDates] = useState(null);
  const [date, setDate] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [planner, setPlanner] = useState('');
  const [slot, setSlot] = useState('');
  const [calls, setCalls] = useState(new Set());

  useEffect(() => {
    api.get('/api/climb-check/dates')
      .then(({ data: d }) => {
        setDates(d.dates || []);
        const tomorrow = new Date(Date.now() + 864e5).toISOString().slice(0, 10);
        // The list now runs from last week to a month ahead (oldest first).
        const today = new Date().toLocaleDateString('en-CA');
        const list = d.dates || [];
        const pick = list.find((x) => x.job_date === tomorrow) || list.find((x) => x.job_date >= today) || list[list.length - 1];
        if (pick) setDate(pick.job_date);
      })
      .catch((e) => setError(e?.response?.data?.error || 'Could not load Climb Check.'));
  }, []);

  // ODIN reads (20/10/2026): the laptop script's last runs, and a refresh request.
  const [odin, setOdin] = useState(null);
  const [refreshNote, setRefreshNote] = useState('');
  const loadRuns = () => api.get('/api/climb-check/odin/runs').then(({ data: d }) => setOdin(d)).catch(() => {});
  useEffect(() => { loadRuns(); const t = setInterval(loadRuns, 60000); return () => clearInterval(t); }, []);
  const askRefresh = () => {
    setRefreshNote('');
    api.post('/api/climb-check/odin/refresh-request', { date })
      .then(() => { setRefreshNote(`Asked for a fresh ODIN read of ${ukDate(date)}. The laptop picks it up within about 2 minutes, then new jobs are surveyed.`); loadRuns(); })
      .catch((e) => setRefreshNote(e?.response?.data?.error || 'Could not ask for a refresh.'));
  };

  const load = () => {
    if (!date) return;
    setLoading(true); setError('');
    api.get('/api/climb-check', { params: { date } })
      .then(({ data: d }) => setData(d))
      .catch((e) => setError(e?.response?.data?.error || 'Could not load the jobs.'))
      .finally(() => setLoading(false));
    loadRuns();
  };
  useEffect(load, [date]);
  // A survey saved in the Pole Survey tab comes back here: reload when that tab
  // says so, and whenever this tab is looked at again.
  useEffect(() => {
    if (!date) return undefined;
    const onShow = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onShow);
    let bc = null;
    try { bc = new BroadcastChannel('climb-check'); bc.onmessage = load; } catch { /* old browser */ }
    return () => { document.removeEventListener('visibilitychange', onShow); bc?.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const jobs = data?.jobs || [];
  const planners = useMemo(() => [...new Set(jobs.map((j) => j.planner))].sort(), [jobs]);
  const scoped = jobs.filter((j) => (!planner || j.planner === planner) && (!slot || j.slot === slot));
  const overhead = scoped.filter((j) => j.call_result !== 'underground');
  const counts = {};
  overhead.forEach((j) => { const k = callOf(j) || 'pending'; counts[k] = (counts[k] || 0) + 1; });
  const underground = scoped.length - overhead.length;
  const pending = overhead.filter((j) => !j.surveyed_at).length;
  const manual = overhead.filter((j) => j.survey_id).length;
  const shown = overhead
    .filter((j) => !calls.size || calls.has(callOf(j)))
    .sort((a, b) => ORDER.indexOf(callOf(a)) - ORDER.indexOf(callOf(b)) || String(a.slot).localeCompare(String(b.slot)));
  const toggle = (k) => setCalls((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  return (
    <div className="p-4 sm:p-6">
      <PageHeader
        title="Climb Check"
        subtitle="Tomorrow's jobs: whether the crew can climb, needs a hoist, or should run the drop via another pole."
        onRefresh={load}
        fetching={loading}
      >
        {dates && dates.length ? (
          <select value={date} onChange={(e) => setDate(e.target.value)} className={selectClass} aria-label="Job date">
            {dates.map((d) => <option key={d.job_date} value={d.job_date}>{weekday(d.job_date)} {ukDate(d.job_date)} · {d.jobs} jobs</option>)}
          </select>
        ) : null}
      </PageHeader>

      <OdinStatus odin={odin} date={date} onRefresh={askRefresh} note={refreshNote} />
      {error ? <p className="mt-4 text-sm font-medium text-red-600">{error}</p> : null}
      {dates && !dates.length ? (
        <p className="mt-6 rounded-lg border border-dashed border-gray-300 bg-white p-6 text-sm text-gray-500">
          No jobs loaded yet. Tomorrow&apos;s jobs appear here once they have been loaded and surveyed.
        </p>
      ) : null}
      <States isLoading={loading && !data} isError={false} what="the jobs" />

      {data ? (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <select value={planner} onChange={(e) => setPlanner(e.target.value)} className={selectClass} aria-label="Planner">
              <option value="">All planners ({planners.length})</option>
              {planners.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select value={slot} onChange={(e) => setSlot(e.target.value)} className={selectClass} aria-label="Slot">
              <option value="">AM and PM</option>
              <option value="AM">AM</option>
              <option value="PM">PM</option>
            </select>
            {ORDER.filter((k) => counts[k]).map((k) => (
              <button
                key={k} type="button" onClick={() => toggle(k)} aria-pressed={calls.has(k)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${calls.has(k) ? 'border-gray-800' : 'border-gray-200'} bg-white`}
              >
                <span className="h-2 w-2 rounded-full" style={{ background: CALLS[k].colour }} />
                {CALLS[k].label} <span className="tabular-nums text-gray-500">{counts[k]}</span>
              </button>
            ))}
            <span className="text-xs text-gray-500">{underground} underground not shown{pending ? ` · ${pending} still being surveyed` : ''}{manual ? ` · ${manual} surveyed by hand` : ''}</span>
          </div>

          <div className="mt-3">
            <Panel title={`${shown.length} overhead job${shown.length === 1 ? '' : 's'}`}>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Slot</th>
                      <th className="px-3 py-2">Job</th>
                      <th className="px-3 py-2">Call</th>
                      <th className="px-3 py-2">Pole and route</th>
                      <th className="px-3 py-2">Why</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {shown.map((j) => {
                      const cbt = j.detail?.cbt;
                      const legs = j.detail?.route?.legs || [];
                      return (
                        // A manager's survey stands out (Ruan, 19/10/2026): tinted row, brand outline, tick.
                        <tr key={j.id} className={`align-top ${j.survey_id ? 'bg-[#faf5fc] outline outline-2 -outline-offset-2 outline-[#711C91]' : ''}`}>
                          <td className={`whitespace-nowrap px-3 py-2 text-gray-600 ${j.survey_id ? 'border-l-4 border-l-[#711C91]' : ''}`}>{j.slot || '—'}</td>
                          <td className="px-3 py-2">
                            <p className="font-medium text-gray-900">{titleCase(j.address)}</p>
                            <p className="font-mono text-xs text-gray-500">{j.wo} · {j.planner}</p>
                          </td>
                          <td className="px-3 py-2">
                            {j.survey_id ? (
                              <div className="flex flex-col items-start gap-1">
                                <span className="inline-flex items-center gap-1">
                                  <CheckCircleIcon className="h-5 w-5 text-[#067647]" aria-hidden="true" />
                                  <CallPill call={callOf(j)} />
                                </span>
                                <p className="text-[11px] font-semibold text-[#711C91]">
                                  Surveyed: {SURVEY_LABEL[j.survey_outcome] || j.survey_outcome}
                                  {j.survey_by ? ` · ${j.survey_by}` : ''} · {ukDateTime(j.survey_at)}
                                </p>
                                {callOf(j) !== j.call_result && j.call_result ? (
                                  <p className="text-[11px] text-gray-400">was {(CALLS[j.call_result] || CALLS.check).label}</p>
                                ) : null}
                              </div>
                            ) : j.surveyed_at ? (
                              <div className="flex flex-col items-start gap-1">
                                <CallPill call={j.call_result} />
                                {j.call_result === 'check' && CHECK_REASON[j.detail?.check_reason] ? (
                                  <p className="text-[11px] font-medium text-gray-600">{CHECK_REASON[j.detail.check_reason]}</p>
                                ) : null}
                              </div>
                            ) : <span className="text-xs text-gray-400">surveying…</span>}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-700">
                            {j.cbt_pole ? <p className="font-mono">{j.cbt_pole}{j.l4_closure ? <span className="text-gray-400"> · L4 {j.l4_closure}</span> : null}</p> : null}
                            {j.route === 'via' ? (
                              <p className="font-semibold text-violet-800">via {j.via_pole} · {legs.map((l) => l.distance_m).join(' + ')} m</p>
                            ) : legs.length ? <p className="text-gray-500">direct · {legs[0].distance_m} m</p> : null}
                          </td>
                          <td className="max-w-md px-3 py-2 text-xs text-gray-700">
                            {j.summary}
                            {j.detail?.history?.length ? (
                              // Approved MEWP requests at this CBT / house / pole, last 18 months.
                              <p className="mt-1 font-medium text-amber-800">
                                Support history: {j.detail.history.map((h) => `${ukDay(h.date)} ${h.reason} (${h.match})`).join(' · ')}
                              </p>
                            ) : null}
                            {j.survey_notes ? <p className="mt-1 text-gray-900">Survey note: {j.survey_notes}</p> : null}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-xs">
                            <div className="flex flex-col gap-1">
                              {cbt && cbt.latitude ? (
                                <a className="font-medium text-[#711C91] hover:underline" target="_blank" rel="noopener noreferrer"
                                  href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${cbt.latitude},${cbt.longitude}`}>Street View</a>
                              ) : null}
                              {/* Opens this job's pole on the Pole Survey page; a survey saved there comes back onto the job. */}
                              <Link className="font-medium text-[#711C91] hover:underline" to={`/pole-survey?cc=${j.id}`} target="_blank" rel="noopener">
                                {j.survey_id ? 'Pole map' : 'Survey on pole map'}
                              </Link>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {!shown.length ? <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-gray-400">Nothing matches these filters.</td></tr> : null}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        </>
      ) : null}
    </div>
  );
}
